import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { API_PATH } from 'config';
import { getAccessTokenApi } from 'api/auth';

const DEFAULTS = {
  estado: 'ACTIVA',
  orden: 'prioridad',
  page: 1,
  pageSize: 25,
};

/**
 * Listado de hallazgos agrupados por problema. La fila es el problema, no la
 * detección: `id_hallazgo_vivo` es el hallazgo sobre el que operan las acciones.
 */
export default function useHallazgoGrupos(initial = {}) {
  const [filters, setFilters] = useState({ ...DEFAULTS, ...initial });
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '' || v === false) return;
        params[k] = Array.isArray(v) ? v.join(',') : v;
      });
      const res = await axios.get(`${API_PATH}/actividades-hallazgo/grupos`, {
        params,
        headers: { Authorization: getAccessTokenApi() },
        signal: controller.signal,
      });
      setData(res.data || { rows: [], total: 0 });
    } catch (err) {
      // Un request abortado no es un fallo real: lo dispara la propia
      // consulta siguiente al cambiar los filtros, no hay nada que informar.
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      message.error('No se pudo cargar el listado de problemas');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  const updateFilter = useCallback(
    (patch) => setFilters((prev) => ({ ...prev, ...patch, page: 1 })),
    []
  );

  return useMemo(
    () => ({ data, loading, filters, setFilters, updateFilter, refetch: fetchData }),
    [data, loading, filters, updateFilter, fetchData]
  );
}
